import { IPAPI_KEY } from "./weatherBackground";

const IPV4_RE = /^(\d{1,3}\.){3}\d{1,3}$/;
const IPV6_RE = /^[0-9a-fA-F:]+$/;

const IPV4_PRIVATE = [
  /^10\./,
  /^192\.168\./,
  /^172\.(1[6-9]|2\d|3[01])\./,
  /^127\./,
  /^169\.254\./,
];
const IPV6_PRIVATE = [
  /^::1$/i,
  /^fc[0-9a-f]{2}:/i,
  /^fd[0-9a-f]{2}:/i,
  /^fe80:/i,
];

const MEMORY_CACHE = new Map();
const STORAGE_KEY = "lunova.ipLookup.v1";
const SUCCESS_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const FAILURE_TTL_MS = 15 * 60 * 1000;
const RATE_LIMIT_COOLDOWN_MS = 15 * 60 * 1000;
const MIN_REQUEST_GAP_MS = 350;
const MAX_CONCURRENT = 2;

let inFlight = 0;
let lastRequestAt = 0;
let rateLimitedUntil = 0;
const waitQueue = [];

function readPersistentCache() {
  if (typeof window === "undefined" || !window.localStorage) {
    return {};
  }

  try {
    return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "{}") || {};
  } catch {
    return {};
  }
}

function writePersistentCache(cache) {
  if (typeof window === "undefined" || !window.localStorage) return;

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(cache));
  } catch {
    // Ignore quota / private-mode failures.
  }
}

function getCachedLookup(ip) {
  const memoryHit = MEMORY_CACHE.get(ip);

  if (memoryHit && memoryHit.expiresAt > Date.now()) {
    return memoryHit.value;
  }

  const persistent = readPersistentCache();
  const stored = persistent[ip];

  if (stored?.expiresAt > Date.now()) {
    MEMORY_CACHE.set(ip, stored);

    return stored.value;
  }

  return null;
}

function setCachedLookup(ip, value, ttlMs) {
  const entry = {
    value,
    expiresAt: Date.now() + ttlMs,
  };

  MEMORY_CACHE.set(ip, entry);

  const persistent = readPersistentCache();

  persistent[ip] = entry;
  writePersistentCache(persistent);
}

function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

async function withLookupSlot(task) {
  if (inFlight >= MAX_CONCURRENT) {
    await new Promise((resolve) => {
      waitQueue.push(resolve);
    });
  }

  inFlight += 1;

  try {
    const waitMs = Math.max(
      0,
      MIN_REQUEST_GAP_MS - (Date.now() - lastRequestAt)
    );

    if (waitMs > 0) {
      await sleep(waitMs);
    }

    lastRequestAt = Date.now();

    return await task();
  } finally {
    inFlight -= 1;
    const next = waitQueue.shift();

    if (next) next();
  }
}

export function isPrivateIp(ip) {
  if (!ip || typeof ip !== "string") return false;
  if (IPV4_RE.test(ip)) return IPV4_PRIVATE.some((r) => r.test(ip));
  if (IPV6_RE.test(ip)) return IPV6_PRIVATE.some((r) => r.test(ip));

  return false;
}

async function fetchIpApi(ip, signal) {
  const res = await fetch(
    `https://ipapi.co/${encodeURIComponent(ip)}/json/?key=${IPAPI_KEY}`,
    { signal }
  );

  if (res.status === 429) {
    rateLimitedUntil = Date.now() + RATE_LIMIT_COOLDOWN_MS;
    throw Object.assign(new Error("ipapi-rate-limited"), { status: 429 });
  }

  if (!res.ok) {
    throw Object.assign(new Error("lookup-failed"), { status: res.status });
  }

  const data = await res.json();

  if (data.error) {
    throw new Error("lookup-failed");
  }

  return {
    ip: data.ip,
    version: data.version,
    city: data.city,
    region: data.region,
    country: data.country_name,
    countryCode: data.country_code,
    postal: data.postal,
    latitude: data.latitude,
    longitude: data.longitude,
    timezone: data.timezone,
    utcOffset: data.utc_offset,
    asn: data.asn,
    org: data.org,
  };
}

async function fetchIpWhoIs(ip, signal) {
  const res = await fetch(`https://ipwho.is/${encodeURIComponent(ip)}`, {
    signal,
  });

  if (!res.ok) {
    throw Object.assign(new Error("lookup-failed"), { status: res.status });
  }

  const data = await res.json();

  if (!data?.success) {
    throw new Error("lookup-failed");
  }

  return {
    ip: data.ip,
    city: data.city,
    region: data.region,
    country: data.country,
    countryCode: data.country_code,
    postal: data.postal,
    latitude: data.latitude,
    longitude: data.longitude,
    timezone: data.timezone?.id,
    org: data.connection?.org,
  };
}

export async function lookupIp(ip, { timeoutMs = 6000 } = {}) {
  if (isPrivateIp(ip)) return { ip, private: true };

  const cached = getCachedLookup(ip);

  if (cached) return cached;

  if (Date.now() < rateLimitedUntil) {
    return { ip, error: "rate-limited" };
  }

  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), timeoutMs);

  try {
    const result = await withLookupSlot(async () => {
      try {
        return await fetchIpApi(ip, ctrl.signal);
      } catch (error) {
        if (error?.status === 429) {
          throw error;
        }

        // Fallback provider when ipapi is unavailable (but not when rate-limited
        // cooldown was already set above).
        return fetchIpWhoIs(ip, ctrl.signal);
      }
    });

    setCachedLookup(ip, result, SUCCESS_TTL_MS);

    return result;
  } catch (error) {
    const failure = {
      ip,
      error: error?.status === 429 ? "rate-limited" : "lookup-failed",
    };

    setCachedLookup(ip, failure, FAILURE_TTL_MS);

    return failure;
  } finally {
    clearTimeout(t);
  }
}

export function flagEmoji(cc) {
  if (!cc || cc.length !== 2) return "";
  const offset = 0x1f1e6 - "A".charCodeAt(0);

  return String.fromCodePoint(
    ...[...cc.toUpperCase()].map((c) => c.charCodeAt(0) + offset)
  );
}
